import React, { useCallback, useMemo } from "react";
import { get } from "lodash";
import { FieldArray } from "react-final-form-arrays";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import classNames from "classnames";
import SectionHeader from "rs-components/SectionHeader";
import { ButtonStyle } from "components/Button";
import Parameter from "../Parameter";
import { SimulationFormFields, VariantSourceType } from "../../../..";
import { VariantLabels } from "shared/labels";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { workspaceCellModelsSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { isVariantCellModelParameterCompleted } from "store/rs-workspace/utils/completeness.utils";
import { getCellModelParametersToOptimizeList } from "store/cell-model/cell-model.utils";
import { checkRecipeParameterIsCompleted } from "store/recipe/recipe.utils";
import { isNotEmpty } from "utils/validate";
import * as styles from "./styles.module.scss";
const ParameterSection = ({ type, name, restrictions, onVariantConfigurationFieldChange }) => {
    const form = useForm();
    const formState = useFormState();
    const editable = useSelector(isEditableSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const workspaceCellModels = useSelector(workspaceCellModelsSelector);
    const formParameters = useMemo(() => get(formState.values, name), [name, formState.values]);
    const hasUnfinishedParameter = useMemo(() => formParameters?.some((item) => {
        if (type === VariantSourceType.CellModel) {
            return Boolean(item?.parameter?.notSaved);
        }
        return Boolean(item?.notSaved);
    }), [formParameters, type]);
    const formRecipe = useMemo(() => get(formState.values, SimulationFormFields.recipe), [formState.values]);
    const formCellModel = useMemo(() => get(formState.values, SimulationFormFields.cellModel), [formState.values]);
    const parameterParentEntity = useMemo(() => {
        if (type === VariantSourceType.CellModel) {
            return workspaceCellModels?.find(({ id }) => id === formCellModel?.value);
        }
        return workspaceRecipes?.find(({ id }) => id === formRecipe?.value);
    }, [workspaceRecipes, workspaceCellModels, type, formRecipe, formCellModel]);
    const allParametersUsed = useMemo(() => {
        const recipeParameters = workspaceRecipes?.find(({ id }) => id === formRecipe?.value)?.state?.parameters ?? [];
        const allParameters = type === VariantSourceType.Recipe
            ? recipeParameters
            : getCellModelParametersToOptimizeList(parameterParentEntity?.state?.parameters ?? {}, []);
        return (!hasUnfinishedParameter && allParameters?.length > 0 && allParameters?.length === formParameters?.length);
    }, [
        formParameters,
        parameterParentEntity,
        formRecipe?.value,
        type,
        workspaceRecipes,
        hasUnfinishedParameter,
    ]);
    const isRefinedCellModel = useMemo(() => type === VariantSourceType.CellModel && parameterParentEntity?.refined, [type, parameterParentEntity]);
    const customAddParameterMessage = useMemo(() => type === VariantSourceType.Recipe
        ? VariantLabels.ADD_RECIPE_PARAMETER
        : VariantLabels.ADD_CELL_MODEL_PARAMETER, [type]);
    const plusButtonDisabledTooltip = useMemo(() => {
        if (allParametersUsed) {
            return VariantLabels.ALL_PARAMETERS_USED;
        }
        if (isRefinedCellModel) {
            return VariantLabels.NO_REFINED_CELL_MODEL_PARAMETERS;
        }
        if (hasUnfinishedParameter) {
            return type === VariantSourceType.Recipe
                ? VariantLabels.COMPLETE_RECIPE_PARAMETER
                : VariantLabels.COMPLETE_CELL_MODEL_PARAMETER;
        }
    }, [allParametersUsed, hasUnfinishedParameter, isRefinedCellModel, type]);
    const isCellModelParameterIncomplete = useCallback((parameter) => {
        if (type !== VariantSourceType.CellModel)
            return false;
        const cellModelParametersList = parameterParentEntity?.state?.adjustmentParameters ?? [];
        const selectedCellModelParameter = cellModelParametersList?.find(({ id }) => id === parameter?.parameterId);
        return (!!selectedCellModelParameter &&
            !!selectedCellModelParameter?.uncertainty &&
            !isVariantCellModelParameterCompleted({
                parameter: {
                    ...selectedCellModelParameter.uncertainty,
                    parameterId: selectedCellModelParameter.id ?? "",
                },
            }));
    }, [parameterParentEntity, type]);
    const isRecipeParameterIncomplete = useCallback((parameter) => {
        if (type !== VariantSourceType.Recipe)
            return false;
        const selectedRecipeParameter = parameterParentEntity?.state?.parameters?.find(({ id }) => `${id}` === `${parameter?.id}`);
        if (!selectedRecipeParameter)
            return false;
        return !checkRecipeParameterIsCompleted(selectedRecipeParameter);
    }, [parameterParentEntity, type]);
    const someRecipeParameterIncomplete = useMemo(() => formParameters?.some((item) => isRecipeParameterIncomplete(item)), [formParameters, isRecipeParameterIncomplete]);
    const someCellModelParameterIncomplete = useMemo(() => formParameters?.some((item) => isCellModelParameterIncomplete(item?.parameter)), [formParameters, isCellModelParameterIncomplete]);
    const plusButtonDisabled = useMemo(() => {
        const conditions = [allParametersUsed, hasUnfinishedParameter, isRefinedCellModel];
        conditions.push(type === VariantSourceType.CellModel ? someCellModelParameterIncomplete : someRecipeParameterIncomplete);
        return conditions.some(Boolean);
    }, [
        allParametersUsed,
        hasUnfinishedParameter,
        isRefinedCellModel,
        someRecipeParameterIncomplete,
        someCellModelParameterIncomplete,
        type,
    ]);
    return (React.createElement("div", { key: name, className: styles.parameterSection },
        React.createElement(FieldArray, { name: name, render: (renderProps) => (React.createElement(React.Fragment, null,
                React.createElement(SectionHeader, { title: parameterParentEntity?.name ?? "Not available", editable: editable && isNotEmpty(parameterParentEntity?.name), mainAction: {
                        handler: () => {
                            form.mutators.push(name, type === VariantSourceType.Recipe ? { notSaved: true } : { parameter: { notSaved: true } });
                        },
                        disabled: plusButtonDisabled,
                        disabledTooltip: plusButtonDisabledTooltip,
                        customAddMessage: customAddParameterMessage,
                        style: formParameters?.length === 0 ? ButtonStyle.Primary : ButtonStyle.Secondary,
                    }, notAvailable: !isNotEmpty(parameterParentEntity?.name) }),
                React.createElement("div", { className: classNames(styles["parameterSection-content"], {
                        [styles.empty]: !formParameters?.length,
                    }) },
                    renderProps.fields.map((fieldName, index) => (React.createElement(Parameter, { key: `${fieldName}-${index}-${renderProps.fields.length}`, name: fieldName, renderProps: renderProps, index: index, target: parameterParentEntity, targetType: type, formParameters: formParameters, fieldArrayName: name, restrictions: restrictions, incomplete: type === VariantSourceType.CellModel
                            ? isCellModelParameterIncomplete(renderProps?.fields.value?.[index]?.parameter)
                            : isRecipeParameterIncomplete(renderProps?.fields.value?.[index]), onVariantConfigurationFieldChange: onVariantConfigurationFieldChange }))),
                    !formParameters?.length && (React.createElement("div", { className: styles.emptyHint },
                        React.createElement("p", null, type === VariantSourceType.CellModel
                            ? VariantLabels.CELL_MODEL_PARAMETER_HINT
                            : VariantLabels.RECIPE_PARAMETER_HINT)))))) })));
};
export default ParameterSection;
